import { boolean } from 'yup';
import { apiSlice } from '../apiSlice';

import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	Additional21DetailResDTO,
	AdditionalRequest,
	Day21DetailResDTO,
	Day21PageDTO,
	Day21PageFilter,
	Day21Request,
	Day21SinglePageFilter,
	Day21SingleRequest,
	QuestionnaireDTO,
	Series21DayPageResDTO,
} from '../DTO/exercise/day21.interface';

export const day21ApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getDay21List: builder.query<
			SuccessResponse<Paginated<Day21PageDTO>>,
			Day21PageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/21/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['Day21List'],
		}),
		getDay21SortList: builder.query<SuccessResponse<Day21PageDTO>, null>({
			query: () => ({
				url: `/web/exercise/21/series/sort`,
				method: 'GET',
			}),
		}),
		setDay21SortList: builder.mutation<
			SuccessResponse<any>,
			{ seriesIdList: string[] }
		>({
			query: (body) => ({
				url: `/web/exercise/21/series/sort`,
				method: 'PUT',
				body,
			}),
		}),
		getDay21StatusList: builder.query<SuccessResponse<Day21PageDTO[]>, null>({
			query: () => ({
				url: `/web/exercise/21/series/published`,
				method: 'GET',
			}),
		}),
		setDay21Status: builder.mutation<
			SuccessResponse<any>,
			{ seriesPublishDTOList: { seriesId: string; published: boolean }[] }
		>({
			query: (body) => ({
				url: `/web/exercise/21/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['Day21List', 'Day21Detail'],
		}),
		addDay21: builder.mutation<SuccessResponse<any>, Day21Request>({
			query: (body) => ({
				url: `/web/exercise/21`,
				method: 'POST',
				body,
			}),
		}),
		updateDay21: builder.mutation<SuccessResponse<any>, Day21Request>({
			query: (body) => ({
				url: `/web/exercise/21/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['Day21Detail'],
		}),
		getDay21Detail: builder.query<SuccessResponse<Day21Request>, string>({
			query: (id) => ({
				url: `/web/exercise/21/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['Day21Detail'],
		}),
		getDay21SingleList: builder.query<
			SuccessResponse<Paginated<Series21DayPageResDTO>>,
			Day21SinglePageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/21/series/${body.id}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['Day21SingleList'],
		}),
		deleteDay21: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/21/series/${id}`,
				method: 'DELETE',
			}),
		}),
		getDay21SingleDetail: builder.query<
			SuccessResponse<Day21DetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/21/day/${id}`,
				method: 'GET',
			}),
			providesTags: ['Day21SingleDetail'],
		}),
		updateDay21SingleDetail: builder.mutation<
			SuccessResponse<any>,
			Day21SingleRequest
		>({
			query: (body) => ({
				url: `/web/exercise/21/day/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['Day21SingleDetail'],
		}),
		createAdditional: builder.mutation<SuccessResponse<any>, AdditionalRequest>(
			{
				query: (body) => ({
					url: `/web/exercise/21/day/${body.id}`,
					method: 'POST',
					body,
				}),
			}
		),
		updateAdditional: builder.mutation<SuccessResponse<any>, AdditionalRequest>(
			{
				query: (body) => ({
					url: `/web/exercise/21/additional/${body.id}`,
					method: 'PUT',
					body,
				}),
				invalidatesTags: ['Day21AdditionalDetail'],
			}
		),
		getAdditionalDetail: builder.query<
			SuccessResponse<Additional21DetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/21/additional/${id}`,
				method: 'GET',
				providesTags: ['Day21AdditionalDetail'],
			}),
		}),
		deleteAdditional: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/21/additional/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Day21SingleDetail'],
		}),
		createQuestionnaire: builder.mutation<
			SuccessResponse<any>,
			QuestionnaireDTO
		>({
			query: (body) => ({
				url: `/web/exercise/21/question`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Day21Detail', 'Day21DetailInfo'],
		}),
		getQuestionnaireDetail: builder.query<
			SuccessResponse<QuestionnaireDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/21/question/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['Day21DetailInfo'],
		}),
		deleteQuestionnaire: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/21/question/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Day21Detail', 'Day21DetailInfo'],
		}),
		deleteQuestionnaires: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/21/question/series/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Day21Detail'],
		}),
		getQuestionnaireDetailInfo: builder.query<
			SuccessResponse<QuestionnaireDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/21/question/${id}`,
				method: 'GET',
			}),
		}),
		updateQuestionnaire: builder.mutation<
			SuccessResponse<any>,
			QuestionnaireDTO
		>({
			query: (body) => ({
				url: `/web/exercise/21/question/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['Day21DetailInfo'],
		}),
	}),
});

export const {
	useGetDay21ListQuery,
	useGetDay21SortListQuery,
	useSetDay21SortListMutation,
	useGetDay21StatusListQuery,
	useSetDay21StatusMutation,
	useAddDay21Mutation,
	useUpdateDay21Mutation,
	useGetDay21DetailQuery,
	useGetDay21SingleListQuery,
	useDeleteDay21Mutation,
	useGetDay21SingleDetailQuery,
	useUpdateDay21SingleDetailMutation,
	useCreateAdditionalMutation,
	useGetAdditionalDetailQuery,
	useUpdateAdditionalMutation,
	useDeleteAdditionalMutation,
	useCreateQuestionnaireMutation,
	useGetQuestionnaireDetailQuery,
	useDeleteQuestionnaireMutation,
	useGetQuestionnaireDetailInfoQuery,
	useUpdateQuestionnaireMutation,
	useDeleteQuestionnairesMutation,
} = day21ApiSlice;
